import { gql, type TypedDocumentNode } from '@apollo/client';
import { graphql, type ResultOf } from '@newfront-insurance/graphql-frontend';

export interface SettingsGroup {
  __typename: 'SkillAdministrationGroup';
  uuid: string;
  name: string;
  description: string | null;
  parentGroupUuid: string | null;
  isOrganization: boolean;
  admins: string[];
  managers: string[];
}

// Matches the getUserNovaGroups addition in backend PR #12398's working tree.
// Switch to generated graphql() inference when that contract is deployed.
export const GET_SETTINGS_USER_GROUPS: TypedDocumentNode<
  { getUserNovaGroups: SettingsGroup[] },
  Record<string, never>
> = gql`
  query NovaAdminSettingsUserGroups {
    getUserNovaGroups {
      __typename
      uuid
      name
      description
      parentGroupUuid
      isOrganization
      admins
      managers
    }
  }
`;

// Existing getSkillGroup fields, fetched afresh before a membership write.
export const GET_SETTINGS_ORGANIZATION_MEMBERSHIP = graphql(`
  query NovaAdminSettingsOrganizationMembership($groupId: String!) {
    getSkillGroup(groupId: $groupId) {
      __typename
      uuid
      parentGroupUuid
      members
      admins
      managers
      inheritedRoles
    }
  }
`);

export interface SettingsSkillGroupPerson {
  __typename: 'SkillGroupEmployee';
  userUuid: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
}

export interface SettingsSkillGroupPeopleVariables {
  groupId: string;
  query: string | null;
  emails?: string[];
  first: number;
  after: string | null;
}

// Matches searchSkillGroupPeople in backend PR #12833.
// Switch to generated graphql() inference when that contract is deployed.
export const SEARCH_SETTINGS_SKILL_GROUP_PEOPLE: TypedDocumentNode<
  {
    searchSkillGroupPeople: {
      __typename: 'SkillGroupPeoplePage';
      people: SettingsSkillGroupPerson[];
      totalCount: number;
      nextCursor: string | null;
    };
  },
  SettingsSkillGroupPeopleVariables
> = gql`
  query NovaAdminSettingsSkillGroupPeople(
    $groupId: String!
    $query: String
    $emails: [String!]
    $first: Int
    $after: String
  ) {
    searchSkillGroupPeople(groupId: $groupId, query: $query, emails: $emails, first: $first, after: $after) {
      __typename
      people {
        __typename
        userUuid
        email
        firstName
        lastName
        fullName
      }
      totalCount
      nextCursor
    }
  }
`;

export const ADD_SETTINGS_TEAM_MEMBER = graphql(`
  mutation NovaAdminSettingsAddTeamMember($groupId: String!, $userUuid: String!) {
    addUserToSkillGroup(groupId: $groupId, userUuid: $userUuid, relation: MEMBER) {
      __typename
      uuid
      parentGroupUuid
      members
    }
  }
`);

export const REMOVE_SETTINGS_TEAM_MEMBER = graphql(`
  mutation NovaAdminSettingsRemoveTeamMember($groupId: String!, $userUuid: String!) {
    removeUserFromSkillGroup(groupId: $groupId, userUuid: $userUuid, relation: MEMBER) {
      __typename
      uuid
      parentGroupUuid
      members
    }
  }
`);

export const UNSHARE_SETTINGS_TEAM_CUSTOM_SKILL = graphql(`
  mutation NovaAdminSettingsUnshareTeamCustomSkill($groupId: String!, $skillUuid: String!) {
    group: unshareCustomSkillFromGroup(groupId: $groupId, skillUuid: $skillUuid) {
      __typename
      uuid
      skillUuids
      builtInSkillCommandHandles
    }
  }
`);

export const UNSHARE_SETTINGS_TEAM_BUILT_IN_SKILL = graphql(`
  mutation NovaAdminSettingsUnshareTeamBuiltInSkill($groupId: String!, $commandHandle: String!) {
    group: unshareBuiltInSkillFromGroup(groupId: $groupId, commandHandle: $commandHandle) {
      __typename
      uuid
      skillUuids
      builtInSkillCommandHandles
    }
  }
`);

// Backend PR #12398 introduces this enum and mutation. Infer from generated types after deployment.
export const TRANSITION_SETTINGS_GROUP_USER_ROLE: TypedDocumentNode<
  {
    transitionSkillGroupUserRole: {
      uuid: string;
      parentGroupUuid: string | null;
      admins: string[];
      members: string[];
      managers: string[];
    };
  },
  { groupId: string; userUuid: string; role: 'ADMIN' | 'MEMBER' }
> = gql`
  mutation NovaAdminSettingsTransitionUserRole(
    $groupId: String!
    $userUuid: String!
    $role: SkillGroupTransitionRole!
  ) {
    transitionSkillGroupUserRole(groupId: $groupId, userUuid: $userUuid, role: $role) {
      __typename
      uuid
      parentGroupUuid
      admins
      members
      managers
    }
  }
`;

// Matches the central BFF createSkillGroup contract; local generated introspection predates these arguments.
// This client operation requires a parent so Settings can only create child teams.
export const CREATE_SETTINGS_TEAM: TypedDocumentNode<
  { createSkillGroup: { uuid: string; name: string; description: string | null; parentGroupUuid: string | null } },
  { name: string; description: string | null; parentGroupUuid: string; memberUserUuids: string[] }
> = gql`
  mutation NovaAdminSettingsCreateTeam(
    $name: String!
    $description: String
    $parentGroupUuid: String!
    $memberUserUuids: [String!]!
  ) {
    createSkillGroup(
      name: $name
      description: $description
      parentGroupUuid: $parentGroupUuid
      memberUserUuids: $memberUserUuids
    ) {
      __typename
      uuid
      name
      description
      parentGroupUuid
    }
  }
`;

// Typed explicitly until stage introspection includes backend PR #12405's employee fields.
// Deploy that BFF contract before enabling live reads, then infer this result with graphql().
export const GET_SETTINGS_GROUP_ADMINS: TypedDocumentNode<{ getSkillGroup: SettingsAdminGroup }, { groupId: string }> =
  gql`
    query NovaAdminSettingsGroupAdmins($groupId: String!) {
      getSkillGroup(groupId: $groupId) {
        __typename
        uuid
        name
        parentGroupUuid
        admins
        adminEmployees {
          __typename
          userUuid
          fullName
          email
          imageUrl
        }
      }
    }
  `;

export interface SettingsAdminGroup {
  __typename: 'SkillGroup';
  uuid: string;
  name: string;
  parentGroupUuid: string | null;
  admins: string[];
  adminEmployees: SettingsGroupEmployee[] | null;
}

export interface SettingsGroupEmployee {
  __typename: 'SkillGroupEmployee';
  userUuid: string;
  fullName: string;
  email: string;
  imageUrl: string | null;
}

// Uses the same pending BFF employee-hydration contract as the Admins query above.
export const GET_SETTINGS_TEAM_DETAILS: TypedDocumentNode<{ getSkillGroup: SettingsTeamDetails }, { groupId: string }> =
  gql`
    query NovaAdminSettingsTeamDetails($groupId: String!) {
      getSkillGroup(groupId: $groupId) {
        __typename
        uuid
        name
        parentGroupUuid
        managers
        members
        managerEmployees {
          __typename
          userUuid
          fullName
          email
          imageUrl
        }
        memberEmployees {
          __typename
          userUuid
          fullName
          email
          imageUrl
        }
        skillUuids
        builtInSkillCommandHandles
      }
    }
  `;

export interface SettingsTeamDetails {
  __typename: 'SkillGroup';
  uuid: string;
  name: string;
  parentGroupUuid: string | null;
  managers: string[];
  members: string[];
  managerEmployees: SettingsGroupEmployee[] | null;
  memberEmployees: SettingsGroupEmployee[] | null;
  skillUuids: string[];
  builtInSkillCommandHandles: string[];
}

// Load the existing group-authorized skill records independently of the team's people and counts.
export const GET_SETTINGS_TEAM_SKILLS = graphql(`
  query NovaAdminSettingsTeamSkills($groupId: String!) {
    getSkillGroup(groupId: $groupId) {
      __typename
      uuid
      skills {
        __typename
        uuid
        command
        frontmatter {
          __typename
          summary
          description
        }
        owner {
          __typename
          firstName
          lastName
          email
        }
      }
    }
  }
`);

export type SettingsTeamSkill = ResultOf<typeof GET_SETTINGS_TEAM_SKILLS>['getSkillGroup']['skills'][number];
